import { createContext, useContext, useEffect, useState } from 'react';
import { setCSRFToken, setCSPHeaders, checkRateLimit } from '../utils/security';

const SecurityContext = createContext();

export const useSecurityContext = () => {
  const context = useContext(SecurityContext);
  if (!context) {
    throw new Error('useSecurityContext must be used within SecurityProvider');
  }
  return context;
};

export const SecurityProvider = ({ children }) => {
  const [isSecurityInitialized, setIsSecurityInitialized] = useState(false);
  const [securityConfig, setSecurityConfig] = useState({
    csrfEnabled: true,
    rateLimitEnabled: true,
    xssProtectionEnabled: true,
  });

  useEffect(() => {
    // Initialize security measures
    const initializeSecurity = async () => {
      try {
        // Set CSRF token
        if (securityConfig.csrfEnabled) {
          setCSRFToken();
        }

        // Set Content Security Policy - DISABLED in development
        if (securityConfig.xssProtectionEnabled && process.env.NODE_ENV === 'production') {
          setCSPHeaders();
        }

        // Generate unique client ID for rate limiting
        if (!localStorage.getItem('client_id')) {
          const clientId = crypto.getRandomValues(new Uint32Array(1))[0].toString(16);
          localStorage.setItem('client_id', clientId);
        }

        setIsSecurityInitialized(true);
      } catch (error) {
        console.error('Security initialization failed:', error);
      }
    };

    initializeSecurity();
  }, [securityConfig]);

  // Security check function
  const performSecurityCheck = (action, data = {}) => {
    const checks = {
      rateLimit: () => {
        if (!securityConfig.rateLimitEnabled) return true;
        const clientId = localStorage.getItem('client_id') || 'anonymous';
        return checkRateLimit(`${clientId}_${action}`, 50, 60000);
      },
      
      inputValidation: () => {
        // Basic input validation
        if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
          return false;
        }
        return true;
      },
      
      csrfValidation: () => {
        if (!securityConfig.csrfEnabled) return true;
        // CSRF validation would be handled by the server
        return true;
      }
    };

    // Run all security checks
    for (const [checkName, checkFn] of Object.entries(checks)) {
      if (!checkFn()) {
        console.warn(`Security check failed: ${checkName} for action: ${action}`);
        return false;
      }
    }

    return true;
  };

  // Log security events
  const logSecurityEvent = (event, details = {}) => {
    const logEntry = {
      timestamp: new Date().toISOString(),
      event,
      details,
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    // In production, send to security monitoring service
    if (process.env.NODE_ENV === 'production') {
      console.log('Security Event:', logEntry);
    } else {
      console.log('Security Event (Dev):', logEntry);
    }
  };

  const value = {
    isSecurityInitialized,
    securityConfig,
    setSecurityConfig,
    performSecurityCheck,
    logSecurityEvent
  };

  return (
    <SecurityContext.Provider value={value}>
      {children}
    </SecurityContext.Provider>
  );
};

export default SecurityContext;